import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validateState } from '../services/auth';
import { useAuth } from '../contexts/AuthContext';
import './Callback.css';

const Callback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const processedCode = useRef<string | null>(null);

  useEffect(() => {
    let isProcessing = false;

    const handleCallback = async () => {
      const code = searchParams.get('code');

      if (isProcessing) {
        return;
      }

      if (code && processedCode.current === code) {
        return;
      }

      isProcessing = true;
      if (code) {
        processedCode.current = code;
      }

      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        const errorMsg = errorDescription
          ? `Authentication error: ${errorParam} - ${errorDescription}`
          : `Authentication error: ${errorParam}`;
        console.error('Cognito error:', errorParam, errorDescription);
        setError(errorMsg);
        setLoading(false);
        return;
      }

      if (!code || !state) {
        setError('Missing authorization code or state');
        setLoading(false);
        return;
      }

      const isValidState = validateState(state);
      if (!isValidState) {
        console.warn('State validation failed, but continuing anyway');
      }

      try {
        // Exchange code for session (backend creates session and sets cookie)
        const sessionResponse = await exchangeCodeForSession(code);
        
        // Backend sets session_id in HttpOnly cookie automatically
        // Store session_id in localStorage for reference (optional, cookie is the source of truth)
        if (sessionResponse.session_id) {
          localStorage.setItem('session_id', sessionResponse.session_id);
        }

        await new Promise(resolve => setTimeout(resolve, 100));
        await checkAuth();

        navigate('/dashboard');
      } catch (err: any) {
        console.error('Auth callback error:', err);
        setError(err.message || 'Failed to complete authentication');
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, checkAuth]);

  const exchangeCodeForSession = async (code: string) => {
    const { API_URL, FRONTEND_URL } = require('../config/api');
    const redirectUri = `${FRONTEND_URL}/auth/callback`;

    // Call backend endpoint to exchange code for session (handles client secret securely)
    // Backend creates session and sets HttpOnly cookie automatically
    const response = await fetch(`${API_URL}/auth/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important: include cookies
      body: JSON.stringify({
        code: code,
        redirect_uri: redirectUri,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Session creation failed: ${errorText}`);
    }

    const result = await response.json();
    // Backend returns { message, status, data: { session_id } }
    return result.data;
  };

  if (loading) {
    return (
      <div className="callback-container">
        <div className="callback-card">
          <p>Completing authentication...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="callback-container">
        <div className="callback-card error">
          <h2>Authentication Failed</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/login')}>Try Again</button>
        </div>
      </div>
    );
  }

  return null;
};

export default Callback;

